import { clsx } from 'clsx'
import { ButtonHTMLAttributes, forwardRef } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'dark' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', children, disabled, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled}
        className={clsx(
          'inline-flex items-center justify-center font-medium transition-all duration-150 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2',
          {
            // Black ring, not gold: a gold ring on a gold button is invisible,
            // and the design system keeps focus rings neutral everywhere.
            'bg-brand-primary text-black hover:bg-brand-primary-hover focus:ring-brand-dark active:scale-[0.98]':
              variant === 'primary',
            'bg-bg-subtle text-text-primary hover:bg-border-default focus:ring-border-hover':
              variant === 'secondary',
            // redesign signature secondary action (Pro moment, PresenceDock) — black/gold world
            'bg-brand-dark text-white hover:bg-brand-dark/90 focus:ring-brand-dark active:scale-[0.98]':
              variant === 'dark',
            'border border-border-default bg-white text-text-primary hover:border-border-hover hover:bg-bg-page focus:ring-border-hover':
              variant === 'outline',
            'text-text-secondary hover:text-text-primary hover:bg-bg-subtle focus:ring-border-hover':
              variant === 'ghost',
            'bg-danger text-white hover:bg-red-600 focus:ring-danger':
              variant === 'danger',
            'px-3 py-1.5 text-xs': size === 'sm',
            'px-5 py-2.5 text-[13px]': size === 'md',
            'px-6 py-3 text-base': size === 'lg',
            'opacity-50 cursor-not-allowed': disabled,
          },
          className
        )}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
